class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    // Submit on input for most controls, but bubbles inside <price-range> will be guarded in onSubmitHandler
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    // Also submit on change so price inputs (and others) can submit after editing without live re-render while typing
    facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    // Do not auto-submit on 'input' events coming from inside <price-range> to avoid re-render while typing/dragging
    if (event.type === 'input' && event.target && event.target.closest && event.target.closest('price-range')) {
      return;
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const searchParams = new URLSearchParams();

    sortFilterForms.forEach((form) => {
      const formData = new FormData(form);
      for (const [key, value] of formData.entries()) {
        if (key === 'q' || key === 'sort_by' || key.includes('options[')) {
          if (value) searchParams.set(key, value);
        } else {
          searchParams.append(key, value);
        }
      }
    });

    this.onSubmitForm(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    // Currency symbol from data attribute (defaults to $)
    this.currencySymbol = this.dataset.currencySymbol || '$';

    // Cache elements: two ranges and two numeric inputs (bubbles) possibly in a separate wrapper
    this.ranges = Array.from(this.querySelectorAll('.range-wrap .range'));
    this.bubbles = Array.from(this.querySelectorAll('.inputs-wrap .bubble'));
    if (this.bubbles.length === 0) {
      // Fallback to legacy structure where inputs were inside .range-wrap
      this.bubbles = Array.from(this.querySelectorAll('.range-wrap .bubble'));
    }

    // Listen for change on both range and numeric inputs
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    // Initialize pairs by index and bind real-time syncing
    for (let i = 0; i < Math.min(this.ranges.length, this.bubbles.length); i++) {
      const range = this.ranges[i];
      const bubble = this.bubbles[i];
      if (!range || !bubble) continue;

      // Mirror constraints from range to numeric input
      ['min', 'max', 'step'].forEach(attr => {
        if (range.hasAttribute(attr)) bubble.setAttribute(attr, range.getAttribute(attr));
      });

      // Initial sync
      bubble.value = this.formatCurrency(range.value);
      this.setBubble(range, bubble);

      // Range live update -> numeric input and position
      range.addEventListener('input', () => {
        bubble.value = this.formatCurrency(range.value);
        this.setBubble(range, bubble);
        this.updateCrossConstraints();
      });

      // Numeric input live update -> allow free typing; update paired range only when parsable; no formatting here
      bubble.addEventListener('input', () => {
        const numeric = this.parseCurrency(bubble.value);
        if (Number.isFinite(numeric)) {
          const minAttr = Number(bubble.getAttribute('min'));
          const maxAttr = Number(bubble.getAttribute('max'));
          if (!isNaN(minAttr) && numeric < minAttr) {
            // Clamp immediately to min if user typed below allowed value
            bubble.value = this.formatCurrency(minAttr);
            range.value = String(minAttr);
            this.updateCrossConstraints();
            return;
          }
          if (!isNaN(maxAttr) && numeric > maxAttr) {
            // Clamp immediately to max if user typed above allowed value
            bubble.value = this.formatCurrency(maxAttr);
            range.value = String(maxAttr);
            this.updateCrossConstraints();
            return;
          }
          range.value = String(numeric);
        }
        // Do not call setBubble or updateCrossConstraints here to avoid overriding user typing
      });
    }

    // Apply initial cross-constraints
    this.updateCrossConstraints();
  }

  getIndexFromTarget(target) {
    // Determine index (0 for min, 1 for max) based on arrays
    let idx = this.ranges.indexOf(target);
    if (idx === -1) idx = this.bubbles.indexOf(target);
    return idx;
  }

  onRangeChange(event) {
    const target = event.currentTarget;
    const idx = this.getIndexFromTarget(target);
    if (idx === -1) return;
    const range = this.ranges[idx];
    const bubble = this.bubbles[idx];

    // Clamp to own min/max
    this.adjustToValidValues(target);

    // Sync counterpart
    if (target.classList.contains('bubble')) {
      const n = this.parseCurrency(target.value);
      if (range) range.value = Number.isFinite(n) ? String(n) : range.value;
    } else if (target.classList.contains('range')) {
      if (bubble) bubble.value = this.formatCurrency(range.value);
    }

    // Update UI and constraints
    if (range && bubble) this.setBubble(range, bubble);
    this.updateCrossConstraints();
  }

  updateCrossConstraints() {
    if (this.ranges.length < 2 || this.bubbles.length < 2) return;
    const minRange = this.ranges[0];
    const maxRange = this.ranges[1];
    const minInput = this.bubbles[0];
    const maxInput = this.bubbles[1];

    const globalMin = Number(minRange.getAttribute('min') || 0);
    const globalMax = Number(maxRange.getAttribute('max') || 100);
    const half = globalMin + (globalMax - globalMin) / 2;

    // Constrain slider attributes to their halves
    minRange.setAttribute('min', String(globalMin));
    minRange.setAttribute('max', String(half));
    maxRange.setAttribute('min', String(half));
    maxRange.setAttribute('max', String(globalMax));

    // Read and clamp current slider values into their halves
    let minR = Number(minRange.value);
    if (!Number.isFinite(minR)) minR = globalMin;
    minR = Math.max(globalMin, Math.min(minR, half));

    let maxR = Number(maxRange.value);
    if (!Number.isFinite(maxR)) maxR = globalMax;
    maxR = Math.max(half, Math.min(maxR, globalMax));

    // Reflect clamped slider values
    minRange.value = String(minR);
    maxRange.value = String(maxR);

    // Bubble attributes aligned to halves
    minInput.setAttribute('min', String(globalMin));
    minInput.setAttribute('max', String(half));
    maxInput.setAttribute('min', String(half));
    maxInput.setAttribute('max', String(globalMax));

    // Set bubble values with currency formatting based on slider values
    minInput.value = this.formatCurrency(minR);
    maxInput.value = this.formatCurrency(maxR);

    // Update bubble positions
    this.setBubble(minRange, minInput);
    this.setBubble(maxRange, maxInput);
  }

  setBubble(range, bubble) {
    if (!range || !bubble) return;
    const val = range.value;
    const min = range.min ? Number(range.min) : 0;
    const max = range.max ? Number(range.max) : 100;
    const percent = max > min ? ((Number(val) - min) * 100) / (max - min) : 0;

    // Update display value depending on element type with currency formatting
    const formatted = this.formatCurrency(val);
    if (bubble.tagName === 'INPUT') {
      bubble.value = formatted;
    } else {
      bubble.innerHTML = formatted;
    }

    // Position the bubble along the track if styled accordingly
    bubble.style.left = `calc(${percent}% + (${8 - percent * 0.15}px))`;
  }

  setMinAndMaxValues() {
    // Deprecated by updateCrossConstraints but kept for compatibility
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  formatCurrency(value) {
    const n = typeof value === 'string' ? this.parseCurrency(value) : Number(value);
    const safe = Number.isFinite(n) ? n : 0;
    return `${this.currencySymbol}${safe.toFixed(2)}`;
  }

  parseCurrency(value) {
    if (typeof value === 'number') return value;
    if (value == null) return NaN;
    let s = String(value).trim();
    // Remove currency symbol and spaces
    s = s.replace(this.currencySymbol, '').replace(/\s/g, '');
    // Remove thousands separators and keep decimal dot
    s = s.replace(/,/g, '');
    // Keep only valid numeric characters
    s = s.replace(/[^0-9.\-]/g, '');
    if (s === '' || s === '-' || s === '.') return NaN;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : NaN;
  }

  adjustToValidValues(input) {
    const isBubble = input.classList && input.classList.contains('bubble');
    const value = isBubble ? this.parseCurrency(input.value) : Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    let clamped = value;
    if (!isNaN(min) && Number.isFinite(clamped) && clamped < min) clamped = min;
    if (!isNaN(max) && Number.isFinite(clamped) && clamped > max) clamped = max;
    if (!Number.isFinite(clamped)) return;
    if (isBubble) {
      input.value = this.formatCurrency(clamped);
    } else {
      input.value = String(clamped);
    }
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
